import React, { useCallback, useEffect, useState } from 'react'
import { Grid, Typography } from '@mui/material'
import FxLabelView from '../../../../Input/FxLabel/FxLabelView';
import { FxButton } from '../../../../Action/FxButton';
import FxCardHeader from '../../../../Container/FxCardHeader';
import usa_state from '../../../../Utils/usa_states.json'
import FxMaterialSelect from '../../../../Input/FxSelect/FxMaterialSelect';
import { FxTextEditSSN } from '../../../../Input/FxSSN/FxTextEditSSN';
import FxPhoneEdit from '../../../../Input/FxPhone/FxPhoneEdit';
import FxCardFooter from '../../../../Container/FxCardFooter';
import FxCardBody from '../../../../Container/FxCardBody';
import { FxTextEdit } from '../../../../Input/FxText/FxTextEdit';
import { FieldErrorMessageRender } from '../../Common/FieldErrorMessageRender';
import { FxDateEdit } from '../../../../Input/FxDate/FxDateEdit';
import { Divider, Box } from '@mui/material';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import { AUTH_STRINGS } from '../../../../../constants/strings';

import { handleAddressChange, handleDateValidation, setItinValidationOptions, setPhoneValidationOptions, setSsnValidationOptions, ShowInfoToolTip, setCityValidation, setZipValidationOptions, handleZipChange, handleKeyDownChange, handleEmailChange, handleFieldChange, convertToPhoneRequestFormat, deleteUnwantedRequest, convertToPhoneDisplayFormat, fetchDataFromState, updateComponentParams, setEmailValidationOptions } from '../../../../Utils/CommonBaseClass';
import { getKey } from '../../../../../libs/utils/storageManager';
import { useDispatch, useSelector } from 'react-redux';
import { saveNodeData } from '../../../../../libs/utils/autoSaveField';
import { RegisterComponent } from '../../../../../libs/saga/dataSaga';

/**
 * Validation rules for Address Line 1 field:
 * - Required field
 * - Minimum length: 4 characters
 * - Maximum length: 40 characters
 * Uses a shared error message from AUTH_STRINGS for length validation
 */
const setAddressLine1Validation = {
  required: true,
  minLength: {
    value: 4,
    message: AUTH_STRINGS.ERRORS.ADDRESS_LINE_1_LENGTH_VALIDATION
  },
  maxLength: {
    value: 40,
    message: AUTH_STRINGS.ERRORS.ADDRESS_LINE_1_LENGTH_VALIDATION
  }
}
/**

/**
 * Author : Shivaraj PP
 * Component: JointTenancyCoOwnerReviewAndSubmitSignup
 * Description: Review and Submit for Verification for Joint Tenancy Co-Owner Sign Up 
 */
const JointTenancyCoOwnerReviewInfoCardEdit: React.FC<any> = React.memo((props) => {
   let context : any;
    ({ context, props } = RegisterComponent(props));
  const { register, formState: { errors }, handleSubmit, setValue, control, clearErrors, setError, watch, getValues } = useForm({ mode: 'onChange', reValidateMode: 'onChange' });
  const dispatch = useDispatch();
  
 /**
   * fetches the data saved in global state
   */
  const ownerState = useSelector((state: any) =>
    fetchDataFromState(state, 'JT-coOwner-edit-info')
  );
  /**
   * useEffect works when the fetched global state data contain data
   * set the component params for rendering data in form
   */
  useEffect(()=>{
    if(ownerState?.data){
      updateComponentParams(dispatch, props?.id, { 'businessOwnerDetails': {...ownerState?.data} });
    }
  },[ownerState?.data])
  /**
   * This method uses the autoSaved Data to populate in the form
   */
  const updateFormData = useCallback((data: any) => {
    if (!data) return;

    const owner = data;
    const address = owner;
    const taxId = owner;

    const safeSetValue = (field: string, value: any) => {
        if (value !== undefined && value !== null && value !== '') {
            setValue(field, value);
        }
    };

    safeSetValue('firstName', owner?.firstName);
    safeSetValue('lastName', owner?.lastName);
    safeSetValue('middleName', owner?.middleName);
    safeSetValue('dob', owner?.dob);
    safeSetValue('ssn', owner?.ssn);
    safeSetValue('itin', taxId?.id);
    safeSetValue('mobilePhone', convertToPhoneDisplayFormat(owner?.mobilePhone));

    if (!owner?.ssn && taxId?.id) {
        setValue('taxIdentifier', 'itin');
    } else if (owner?.ssn) {
        setValue('taxIdentifier', 'ssn');
    }

    safeSetValue('addressLine1', address?.addressLine1);
    safeSetValue('addressLine2', address?.addressLine2);
    safeSetValue('city', address?.city);
    safeSetValue('state', address?.state);
    safeSetValue('zip', address?.zip);
}, [setValue]);

/**
 * UseEffect to trigger the auto-population of the values
 * retrieved from the autoSave API
 */
useEffect(() => {
    if (props?.autoSavedData) {
        updateFormData(props.autoSavedData[props?.index]);
    }
}, [props?.autoSavedData, updateFormData]);

  let defaultTaxIdentifier: any = ownerState?.data?.taxIdentification ? 'itin' : 'ssn';
  const taxIdentifier = watch('taxIdentifier') || defaultTaxIdentifier;
  const taxIdentifierData = [
    { label: "SSN", value: "ssn" },
    { label: "ITIN", value: "itin" },
  ];
  const dobErrorMsg = {
    futureDate: AUTH_STRINGS.ERRORS.JOINT_TENANCY_FUTURE,
    maxDate: AUTH_STRINGS.ERRORS.JOINT_TENANCY_LESS_THAN_18,
  }

  /**
   * Method Handles the AutoSave Functionality of the DOB Field
   * @param value 
   */
  const handleAutoSave = (fieldName: string, value: any) => {
    saveNodeData('coowner-info-page', fieldName, value, setValue, getValues, dispatch, undefined, props?.index+1);
  }

  /**
   * Method Handles the onChange Event of the DOB Field
   * @param value 
   */
  const handleDOBOnChangeEvents = (value: any) => {
    handleDateValidation("dob", value, setValue, setError, clearErrors, true, moment(), null, dobErrorMsg);
    handleAutoSave("dob", value);
  }

  // Method handles the submit for edit beneficial owner form
  function onSubmit(data: any) {
    if (taxIdentifier === 'ssn') {
      data.ssn = data?.ssn || props?.businessOwnerDetails?.ssn;
    } else {
      data.itin = data?.itin || props?.businessOwnerDetails?.taxIdentification?.idLast4;
    }


    delete data['taxIdentifier'];
    const req: any = { ...data };
    if (data['id'] === '') {
      delete data['id'];
      delete req['id'];
    }
    if (data['dob']) {
      req['dob'] = moment(data['dob']).format('MM/DD/YYYY');
    }
    const address: any = {
      "addressLine1": data['addressLine1'],
      "addressLine2": data['addressLine2'],
      "city": data['city'],
      "state": data['state'],
      "zip": data['zip'],
      "isPrimary": true,
    }
    if (data['address_id']) {
      address['id'] = data['address_id'];
    }
    req['mailingAddress'] = [address]
    req.portalAccess = {
      grantAccess: true
    };
    req['isPrimaryOwner']=false;

    req['mobilePhone'] = convertToPhoneRequestFormat(req.mobilePhone)
    if (req['mobilePhone'] === "") {
      delete req['mobilePhone'];
    }

    if(data.itin && data.itin.length > 4){
      req['taxIdentification'] = {
          'type': 'ITIN',
          'id': data.itin
        }
    }
    const unwantedRequestArray = ['state', 'addressLine1', 'city', 'zip', 'addressLine2', 'creditReport', 'mobilePhone-select', 'itin','address_id'];
    deleteUnwantedRequest(req, unwantedRequestArray)

    if (props.onSubmit) {
      data.isPrimaryOwner = props?.businessOwnerDetails?.isPrimaryOwner
      props.onSubmit(data,req, props?.index)
      if(props?.businessOwnerDetails?.last4ssn){
        req['last4ssn'] = props?.businessOwnerDetails?.last4ssn
      }
      if(props?.businessOwnerDetails?.taxIdentification?.idLast4){
        req['taxIdentification'] = {
          'type': 'ITIN',
          'idLast4': props?.businessOwnerDetails?.taxIdentification?.idLast4
        }
      }
      updateComponentParams(dispatch, props?.id, { 'businessOwnerDetails': {...req} });
    }
    if (props?.cancel) {
      props.cancel()
    }
  }

  return (
    <>
      {props?.businessOwnerDetails  && <Grid className='fx-joint-tenancy-review-box'>
        <FxCardHeader>
          <Typography className='fx-new-invite-owner-profile-label'>{props?.index + 1}. Co-Owner Information</Typography>
        </FxCardHeader>
        <div className="fx-form-edit-profile">
        <form id="signUp-business-owner-form" onSubmit={handleSubmit(onSubmit)}>
          <FxCardBody id="signUp-business-owner-form-card-body" className="fx-info-card" >
            <Grid container direction="row" spacing={2} className="fx-info-card-form" >
              <FxTextEdit register={{ ...register("id") }} className={"fx-hidden"} control={control} rules={{ required: false }} id="signUp-business-owner-form-card-first-name" name="id" variant="outlined" defaultValue={props?.businessOwnerDetails?.id} />
              <Grid item xs={12} sm={12}>
                <FxTextEdit register={{ ...register("firstName") }} className={errors.firstName ? "border-error-input fx-input-edit" : "fx-input-edit"} control={control} rules={{ required: true }} id="signUp-business-owner-form-card-first-name" label="First Name" name="firstName" variant="outlined" defaultValue={props?.businessOwnerDetails?.firstName}
                  onBlur={(e) => handleAutoSave('firstName', e)} />
                <FieldErrorMessageRender error={errors} fieldKey='firstName' />
              </Grid>
              <Grid item xs={12} sm={12}>
                <FxTextEdit register={{ ...register("middleName") }} className={errors.lastname ? "border-error-input fx-input-edit" : "fx-input-edit"} control={control} rules={{ required: false }} id="signUp-business-owner-form-card-middle-name" label="Middle Name (optional)" name="middleName" variant="outlined" defaultValue={props?.businessOwnerDetails?.middleName}
                  onBlur={(e) => handleAutoSave('middleName', e)} />
              </Grid>
              <Grid item xs={12} sm={12}>
                <FxTextEdit register={{ ...register("lastName") }} className={errors.lastname ? "border-error-input fx-input-edit" : "fx-input-edit"} control={control} rules={{ required: true }} id="signUp-business-owner-form-card-last-name" label="Last Name" name="lastName" variant="outlined" defaultValue={props?.businessOwnerDetails?.lastName}
                  onBlur={(e) => handleAutoSave('lastName', e)} />
                <FieldErrorMessageRender error={errors} fieldKey='lastName' />
              </Grid>

              <Grid item sm={12} xs={12}>
                <FxDateEdit register={{ ...register("dob") }} className={errors.dob ? "border-error-input fx-input-edit" : "fx-input-edit"} control={control} rules={{ required: true }} id="signUp-business-owner-form-card-dob" label="Date of Birth" name="dob" variant="outlined" defaultValue={props?.businessOwnerDetails?.dob} maxDate={moment()} setValue={setValue} onChange={(value) => handleDOBOnChangeEvents(value)} ageLimitMessage={dobErrorMsg.maxDate} futureDateMessage={dobErrorMsg.futureDate} />
                <FieldErrorMessageRender error={errors} fieldKey='dob' type='date' />
              </Grid>

              <Grid item xs={12} sm={6} >
                <FxMaterialSelect
                  register={{ ...register("taxIdentifier") }}
                  control={control}
                  rules={{ required: false }}
                  id="signUp-indivudal-form-card-tax-identifier"
                  name="taxIdentifier"
                  data={taxIdentifierData}
                  readOnly={true}
                  value={defaultTaxIdentifier}
                  label="Identification Method"
                  setValue={setValue}
                />
              </Grid>
              <Grid container item xs={12} sm={6} display="flex">
                <Grid container item xs={12} alignItems="center">
                  <Grid item xs={11} sm={11}>
                      <FxTextEditSSN
                        key={taxIdentifier}
                        register={{ ...register(taxIdentifier) }}
                        className={errors.ssn || errors.itin ? "border-error-input fx-input-edit" : "fx-input-edit"}
                        error={errors?.ssn || errors?.itin}
                        control={control}
                        rules={taxIdentifier === 'ssn' ? { ...setSsnValidationOptions(true) } : { ...setItinValidationOptions(true) }}
                        id="signUp-indivudal-form-card-ssn-itin"
                        label={`${taxIdentifier === 'ssn' ? 'Social Security Number' : 'Individual Taxpayer Identification Number'}`}
                        placeholder="XXX-XX-XXXX"
                        name={taxIdentifier}
                        variant="outlined"
                        setValue={setValue}
                        setError={setError}
                        clearErrors={clearErrors}
                        defaultValue={props?.businessOwnerDetails?.last4ssn ? props?.businessOwnerDetails?.last4ssn : props?.businessOwnerDetails?.taxIdentification?.idLast4}
                        onBlur={(e: any) =>
                          handleAutoSave(taxIdentifier, e)
                        }
                      />
                  </Grid>
                  <Grid item xs={1} container justifyContent="center" alignItems="center">
                    {ShowInfoToolTip('fx-business-owner-title-tooltip', 'We will use tax identifier to verify your identity and will not check your credit score', 'bottom-end')}
                  </Grid>
                </Grid>
                <Grid item xs={11} sm={11}>
                  <FieldErrorMessageRender error={errors} fieldKey={taxIdentifier === 'ssn' ? 'ssn' : 'itin'} />
                </Grid>
              </Grid>
              <Grid item xs={12} sm={12}>
                <FxLabelView id="business-owner-details-label" className="" >YOUR CONTACT INFO</FxLabelView>
              </Grid>
              <FxTextEdit register={{ ...register("address_id") }} className={"fx-hidden"} control={control} rules={{ required: false }} id="signUp-business-owner-form-card-first-name" name="address_id" variant="outlined" defaultValue={props?.businessOwnerDetails?.mailingAddress?.[0]?.id} />
              <Grid item xs={12} sm={12}>
                <FxTextEdit register={{ ...register("addressLine1") }} className={errors.addressLine1 ? "border-error-input fx-input-edit" : "fx-input-edit"} control={control} rules={setAddressLine1Validation} id="signUp-business-owner-addressLine1" label="Address Line 1" name="addressLine1" defaultValue={props?.businessOwnerDetails?.mailingAddress?.[0]?.addressLine1} onChange={(e: any) => { handleAddressChange(e, setValue, setError, clearErrors) }}
                  onBlur={(e) => handleAutoSave('addressLine1', e)} />
                <FieldErrorMessageRender error={errors} fieldKey='addressLine1' />
              </Grid>
              <Grid item xs={12} sm={12}>
                <FxTextEdit register={{ ...register("addressLine2") }} className={errors.addressLine2 ? "border-error-input fx-input-edit" : "fx-input-edit"} control={control} rules={{ required: false }} id="signUp-business-owner-addressLine2" label="Address Line 2 (optional)" name="addressLine2" defaultValue={props?.businessOwnerDetails?.mailingAddress?.[0]?.addressLine2}
                  onBlur={(e) => handleAutoSave('addressLine2', e)} />
              </Grid>
              <Grid item xs={12} sm={12}>
                <FxTextEdit register={{ ...register("city") }} className={errors.city ? "border-error-input fx-input-edit" : "fx-input-edit"} control={control} rules={setCityValidation(true)} id="signUp-business-owner-city" label="City" name="city" defaultValue={props?.businessOwnerDetails?.mailingAddress?.[0]?.city}
                  onBlur={(e) => handleAutoSave('city', e)} />
                <FieldErrorMessageRender error={errors} fieldKey='city' />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FxMaterialSelect register={{ ...register("state") }} rules={{ required: true }} className={errors.state ? "border-error-input fx-input-edit" : "fx-input-edit"} control={control} id="signUp-business-owner-select-state" name="state" data={usa_state} label="State" setValue={setValue} value={props?.businessOwnerDetails?.mailingAddress?.[0]?.state}  />
                <FieldErrorMessageRender error={errors} fieldKey='state' />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FxTextEdit register={{ ...register("zip") }} className={errors.zip ? "border-error-input fx-input-edit" : "fx-input-edit"} control={control} rules={setZipValidationOptions(true)} id="signUp-business-owner-zip" label="Zip" name="zip" defaultValue={props?.businessOwnerDetails?.mailingAddress?.[0]?.zip} onChange={(e: any) => { handleZipChange(e, setValue, setError, clearErrors) }} onKeyDown={(e: any) => { handleKeyDownChange(e, setValue) }}
                  onBlur={(e) => handleAutoSave('zip', e)} />
                <FieldErrorMessageRender error={errors} fieldKey='zip' />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <FxPhoneEdit register={register} control={control} rules={setPhoneValidationOptions(true)} className={errors.mobilePhone ? "border-error-input fx-input-edit" : "fx-input-edit"} id="signup-mobilePhone" name="mobilePhone" label="Phone Number" defaultCountryCode={getKey('countryCode')} defaultValue={convertToPhoneDisplayFormat(props?.businessOwnerDetails?.mobilePhone)} isEditable={true} setValue={setValue} setError={setError} clearErrors={clearErrors}
                onBlur={(e) => handleAutoSave('mobilePhone', e)}/>
                <FieldErrorMessageRender error={errors} fieldKey='mobilePhone'/>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FxTextEdit register={{ ...register("email") }} type="email" className={errors.email ? "border-error-input fx-input-edit fs-mask" : "fx-input-edit fs-mask"} control={control} rules={setEmailValidationOptions(true)} id="signUp-business-owner-form-card-email" label="Email" name="email" variant="outlined" isEditable={false} defaultValue={props?.businessOwnerDetails?.email} onChange={(e: any) => { handleEmailChange(e, setValue, setError, clearErrors) }}
                  onBlur={(e) => handleAutoSave('email', e)} />
                <FieldErrorMessageRender error={errors} fieldKey='email' />
              </Grid>
              <Grid item xs={12} sm={12}>
                <Divider />
              </Grid>
            </Grid>
          </FxCardBody>
          <FxCardFooter id="signUp-business-owner-Card-Footer" className="fx-footer">
            <Grid container direction="row" justifyContent="flex-end" className="fx-modal-footer">
              <FxButton
                disableRipple={false}
                variant="contained"
                className="fx-button fx-button-cancel"
                id="back-edit-business-owner-btn"
                onClick={props?.onCancel}
              > Cancel
              </FxButton>
              <Grid className='fx-padding-right-15'></Grid>
              <FxButton
                disableRipple={false}
                className="fx-button fx-button-theme"
                id="save-edit-business-owner-btn"
                type="submit"
              > SAVE
              </FxButton>
            </Grid>
          </FxCardFooter>
        </form>
        </div>
      </Grid>}
    </>
  )
})

export default JointTenancyCoOwnerReviewInfoCardEdit